"use client"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { useEffect, useId, useMemo, useState } from "react"
import { ZodError } from "zod"
import { FirebasePhoneLinkError, linkCurrentUserPhoneCredential, requestCurrentUserPhoneVerification } from "../lib/firebase-client.ts"
import { phoneVerificationChallengeExpired } from "../lib/phone-verification-timer.ts"
import type { UserRoute } from "../lib/routes.ts"
import type { OwnedRedemptionLifecycle, OwnedRedemptionLifecycleResponse, OwnedRedemptionQuoteResponse, PhoneCredentialLinkReason, PhoneVerificationStatus } from "../lib/todo26-contract.ts"
import { createTodo26LiveClient, liveProductAffordability, type Todo26LiveBalance, type Todo26LiveClient, Todo26LiveHttpError, Todo26LiveNetworkError, type Todo26LiveProduct } from "../lib/todo26-live-client.ts"
import { ActionFeedbackSheet } from "./action-feedback-sheet.tsx"
import { JourneyLoading } from "./journey-loading.tsx"
import { PointAmount } from "./point-amount.tsx"
import { RedesignProductDetail } from "./product-view.tsx"
import { useManagedDialog } from "./use-managed-dialog.ts"

type Screen =
  | {
      readonly kind: "confirm"
      readonly quote: OwnedRedemptionQuoteResponse
      readonly status: PhoneVerificationStatus
    }
  | {
      readonly kind: "lifecycle"
      readonly redemption: OwnedRedemptionLifecycleResponse
    }
  | {
      readonly kind: "phone"
      readonly status: PhoneVerificationStatus
    }
  | {
      readonly balance: Todo26LiveBalance
      readonly kind: "product"
      readonly product: Todo26LiveProduct
      readonly status: PhoneVerificationStatus
    }
type LoadState =
  | {
      readonly kind: "loading"
    }
  | {
      readonly kind: "loaded"
      readonly screen: Screen
    }
  | {
      readonly error: unknown
      readonly kind: "failed"
    }
const number = new Intl.NumberFormat("ko-KR")
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const safeNext = (value: string | null): string | null => {
  if (value === null || value.startsWith("//")) return null
  const parsed = new URL(value, "https://cashhub.local")
  if (parsed.origin !== "https://cashhub.local") return null
  if (/^\/products\/[0-9a-f-]{36}$/i.test(parsed.pathname) && uuid.test(parsed.pathname.split("/").at(-1) ?? "")) return parsed.pathname
  if (parsed.pathname === "/redeem/confirm" && uuid.test(parsed.searchParams.get("quoteId") ?? "")) return `${parsed.pathname}${parsed.search}`
  return null
}
const loadScreen = async (client: Todo26LiveClient, pathname: string, route: UserRoute, search: string): Promise<Screen> => {
  const query = new URLSearchParams(search)
  if (route.id === "APP-VERIFY-001") return { kind: "phone", status: await client.getPhoneStatus() }
  if (route.id === "APP-SHOP-003") {
    const productId = pathname.split("/").at(-1) ?? ""
    const status = await client.getPhoneStatus()
    const [product, balance] = await Promise.all([client.getProduct(productId), client.getBalance()])
    if (product === null) throw new Todo26LiveHttpError("T19_E_PRODUCT_UNAVAILABLE", 404)
    return { balance, kind: "product", product, status }
  }
  if (route.id === "APP-RDM-001") {
    const quoteId = query.get("quoteId") ?? ""
    const status = await client.getPhoneStatus()
    const quote = await client.getQuote(quoteId)
    return { kind: "confirm", quote, status }
  }
  const redemptionId = query.get("resourceId") ?? ""
  return { kind: "lifecycle", redemption: await client.getRedemption(redemptionId) }
}
const errorCopy = (error: unknown): string => {
  if (error instanceof Todo26LiveNetworkError) return "요청 결과를 확인하지 못했습니다. 같은 요청 식별자로 안전하게 다시 확인할 수 있습니다."
  if (!(error instanceof Todo26LiveHttpError)) return "화면을 불러오지 못했습니다. 잠시 후 다시 확인해 주세요."
  if (error.code === "T10_E_SESSION" || error.code === "T26_WEB_SESSION_REQUIRED") return "로그인이 만료되었습니다. 다시 로그인해 주세요."
  if (error.code === "T19_E_PRODUCT_UNAVAILABLE" || error.code === "T37_QUOTE_NOT_FOUND") return "상품 또는 교환 견적의 유효시간이 끝났습니다. 상품 목록에서 다시 확인해 주세요."
  if (error.code === "KT_PROVIDER_NOT_READY") return "상품 지급 시스템을 준비하고 있어 지금은 교환할 수 없습니다. 포인트는 차감되지 않았습니다. 잠시 후 다시 이용해 주세요."
  if (error.code === "PILOT_LIMIT_EXCEEDED" || error.code === "KT_ISSUANCE_DISABLED" || error.code === "T26_CHECKOUT_DISABLED") return "현재 준비된 발급 한도가 소진되어 교환할 수 없습니다. 포인트는 차감되지 않습니다."
  if (error.code === "INSUFFICIENT_POINTS") return "사용 가능한 포인트가 부족합니다."
  if (error.code.startsWith("T26_PHONE_")) return "상품을 받으려면 휴대전화 확인이 필요합니다."
  return "요청을 처리하지 못했습니다. 잠시 후 다시 확인해 주세요."
}
const reportOperationalFailure = (phase: "LOAD" | "POLL", error: unknown): void => {
  if (error instanceof Todo26LiveHttpError) {
    console.error(`CASHHUB_TODO26_OPERATION_FAILED ${JSON.stringify({ code: error.code, phase, status: error.status, type: "HTTP" })}`)
    return
  }
  if (error instanceof Todo26LiveNetworkError) {
    console.error(`CASHHUB_TODO26_OPERATION_FAILED ${JSON.stringify({ phase, type: "NETWORK" })}`)
    return
  }
  if (error instanceof ZodError) {
    console.error(`CASHHUB_TODO26_OPERATION_FAILED ${JSON.stringify({ issues: error.issues.map((issue) => ({ code: issue.code, path: issue.path.map(String) })), phase, type: "CONTRACT" })}`)
    return
  }
  console.error(`CASHHUB_TODO26_OPERATION_FAILED ${JSON.stringify({ phase, type: error instanceof Error ? error.name : "UNKNOWN" })}`)
}
const PhonePanel = ({ client, initialStatus, next }: { readonly client: Todo26LiveClient; readonly initialStatus: PhoneVerificationStatus; readonly next: string | null }) => {
  const router = useRouter()
  const rawId = useId()
  const recaptchaId = `cashhub-phone-recaptcha-${rawId.replaceAll(":", "")}`
  const [status, setStatus] = useState(initialStatus)
  const [changing, setChanging] = useState(false)
  const [phone, setPhone] = useState("")
  const [code, setCode] = useState("")
  const [verificationId, setVerificationId] = useState<string | null>(null)
  const [verificationExpiresAt, setVerificationExpiresAt] = useState(0)
  const [expiresInSeconds, setExpiresInSeconds] = useState(0)
  const [cooldownUntil, setCooldownUntil] = useState(0)
  const [cooldownSeconds, setCooldownSeconds] = useState(0)
  const [pending, setPending] = useState(false)
  const [message, setMessage] = useState("")
  const requiresForm = status.verificationState !== "VERIFIED" || changing
  const linkReason: PhoneCredentialLinkReason = changing ? "NUMBER_CHANGE" : status.verificationState === "REVERIFICATION_REQUIRED" ? "REVERIFICATION" : "INITIAL_VERIFICATION"
  useEffect(() => {
    const update = (): void => {
      const now = Date.now()
      setCooldownSeconds(Math.max(0, Math.ceil((cooldownUntil - now) / 1000)))
      setExpiresInSeconds(Math.max(0, Math.ceil((verificationExpiresAt - now) / 1000)))
    }
    update()
    const timer = window.setInterval(update, 1000)
    return () => window.clearInterval(timer)
  }, [cooldownUntil, verificationExpiresAt])
  useEffect(() => {
    if (!phoneVerificationChallengeExpired({ expiresAt: verificationExpiresAt, now: Date.now(), verificationId })) return
    setVerificationId(null)
    setVerificationExpiresAt(0)
    setCode("")
    setMessage("인증번호 입력 시간이 만료되었습니다. 새 인증번호를 요청해 주세요.")
  }, [verificationExpiresAt, verificationId])
  const requestCode = async (): Promise<void> => {
    if (!/^010\d{8}$/.test(phone) || pending) {
      setMessage("010으로 시작하는 휴대전화 번호 11자리를 입력해 주세요.")
      return
    }
    if (cooldownSeconds > 0) {
      setMessage(`${cooldownSeconds}초 후 인증번호를 다시 요청할 수 있습니다.`)
      return
    }
    setPending(true)
    setMessage("")
    try {
      const reservation = await client.reservePhoneVerificationChallenge(phone)
      setCooldownUntil(Date.now() + reservation.cooldownSeconds * 1000)
      const nextVerificationId = await requestCurrentUserPhoneVerification({ containerId: recaptchaId, phoneNumber: `+82${phone.slice(1)}` })
      const nextVerificationExpiresAt = Date.now() + 5 * 60000
      setVerificationExpiresAt(nextVerificationExpiresAt)
      setExpiresInSeconds(5 * 60)
      setVerificationId(nextVerificationId)
      setMessage("인증번호를 발송했습니다. 문자로 받은 6자리 번호를 입력해 주세요.")
    } catch (error: unknown) {
      if (error instanceof Todo26LiveHttpError && error.retryAfterSeconds !== undefined) setCooldownUntil(Date.now() + error.retryAfterSeconds * 1000)
      if (error instanceof Todo26LiveHttpError && error.code === "T26_PHONE_SEND_LIMIT") setMessage("인증번호 요청 한도에 도달했습니다. 안내된 시간이 지난 뒤 다시 시도해 주세요.")
      else if (error instanceof Todo26LiveHttpError && error.code === "T26_PHONE_SEND_COOLDOWN") setMessage("인증번호를 연속으로 요청할 수 없습니다. 잠시 후 다시 시도해 주세요.")
      else setMessage(error instanceof FirebasePhoneLinkError && error.code === "current-user-required" ? "로그인 상태를 다시 확인해 주세요." : "인증번호를 보내지 못했습니다. 잠시 후 다시 시도해 주세요.")
    } finally {
      setPending(false)
    }
  }
  const verify = async (): Promise<void> => {
    if (verificationId === null || verificationExpiresAt <= Date.now() || !/^\d{6}$/.test(code) || pending) {
      setMessage("문자로 받은 인증번호 6자리를 입력해 주세요.")
      return
    }
    setPending(true)
    setMessage("")
    try {
      const transport = await linkCurrentUserPhoneCredential({ linkReason, returnTo: "/my/phone", verificationCode: code, verificationId })
      const verified = await client.completePhoneLink(transport)
      setStatus(verified)
      setChanging(false)
      setPhone("")
      setCode("")
      setVerificationId(null)
      setVerificationExpiresAt(0)
      setMessage("휴대전화 확인이 완료되었습니다.")
      if (next !== null) router.replace(next)
    } catch {
      setMessage("인증을 완료하지 못했습니다. 인증번호와 로그인 계정을 확인해 주세요.")
    } finally {
      setPending(false)
    }
  }
  if (!requiresForm)
    return (
      <section className="todo26-panel" data-phone-verification-state="VERIFIED" data-todo26-phone>
        <header className="todo26-panel-heading">
          <p className="eyebrow">수령 번호</p>
          <h2>휴대전화 확인이 완료되었습니다</h2>
        </header>
        <dl className="detail-grid todo26-details">
          <div>
            <dt>상품을 받을 번호</dt>
            <dd>{status.maskedRecipient}</dd>
          </div>
        </dl>
        <button className="button secondary" onClick={() => setChanging(true)} type="button">
          번호 변경
        </button>
        <p aria-live="polite" className="success-copy">
          {message}
        </p>
      </section>
    )
  return (
    <section className="todo26-panel" data-phone-verification-state={status.verificationState} data-todo26-phone>
      <header className="todo26-panel-heading">
        <p className="eyebrow">안전한 상품 수령</p>
        <h2>{changing ? "새 휴대전화 번호를 확인해 주세요" : "휴대전화 번호를 확인해 주세요"}</h2>
      </header>
      <p>최초 확인, 번호 변경 또는 재확인이 필요한 경우에만 인증번호를 발송합니다.</p>
      <div className="todo26-phone-fields">
        <label>
          <span>휴대전화 번호</span>
          <input
            autoComplete="tel"
            disabled={verificationId !== null}
            inputMode="tel"
            maxLength={11}
            onChange={(event) => {
              setPhone(event.target.value.replaceAll(/[^0-9]/g, ""))
              setVerificationId(null)
              setVerificationExpiresAt(0)
              setCode("")
            }}
            placeholder="01012345678"
            type="tel"
            value={phone}
          />
        </label>
        <button aria-busy={pending} className="button secondary" disabled={pending || cooldownSeconds > 0} onClick={() => void requestCode()} type="button">
          {pending ? "요청 중" : cooldownSeconds > 0 ? `${cooldownSeconds}초 후 다시 받기` : verificationId === null ? "인증번호 받기" : "인증번호 다시 받기"}
        </button>
        {verificationId === null ? null : (
          <>
            <label>
              <span>인증번호</span>
              <input autoComplete="one-time-code" inputMode="numeric" maxLength={6} onChange={(event) => setCode(event.target.value.replaceAll(/[^0-9]/g, ""))} placeholder="6자리" value={code} />
            </label>
            <button aria-busy={pending} className="button primary" disabled={pending} onClick={() => void verify()} type="button">
              {pending ? "확인 중" : "인증 완료"}
            </button>
            <p className="todo26-status-copy">
              인증번호 유효시간 {Math.floor(expiresInSeconds / 60)}분 {String(expiresInSeconds % 60).padStart(2, "0")}초
            </p>
          </>
        )}
      </div>
      <div id={recaptchaId} />
      <p aria-live="polite" className="todo26-status-copy">
        {message}
      </p>
    </section>
  )
}
export const CheckoutCancellationTermsModal = ({ onClose, open }: { readonly onClose: () => void; readonly open: boolean }) => {
  const headingId = useId()
  const descriptionId = useId()
  const { cancel, dialogRef, onDialogClose } = useManagedDialog(open, onClose)
  return (
    <dialog
      aria-describedby={descriptionId}
      aria-labelledby={headingId}
      className="checkout-terms-dialog"
      onCancel={(event) => {
        event.preventDefault()
        cancel()
      }}
      onClose={onDialogClose}
      ref={dialogRef}
    >
      <div className="checkout-terms-content">
        <header className="checkout-terms-heading">
          <div>
            <p className="eyebrow">교환 전 필수 확인</p>
            <h2 id={headingId}>취소·환불 조건</h2>
          </div>
          <button aria-label="취소·환불 조건 닫기" className="checkout-terms-icon-close" onClick={cancel} type="button">
            ×
          </button>
        </header>
        <p id={descriptionId}>교환을 확정하기 전에 아래 내용을 확인해 주세요.</p>
        <ol className="checkout-terms-list">
          <li>교환 확정 전에는 요청을 중단할 수 있으며 포인트가 차감되지 않습니다.</li>
          <li>교환 확정 후 공급사에 발행 요청이 전달되면 원칙적으로 취소·환불 또는 수령번호 변경이 어렵습니다.</li>
          <li>상품 발행에 실패하면 포인트를 확정 차감하지 않으며, 이미 차감된 경우 별도 복원 기록을 남겨 돌려드립니다.</li>
          <li>중복 처리, 시스템 오류 또는 관계 법령상 취소·환불이 필요한 경우에는 예외로 처리합니다.</li>
        </ol>
        <p className="checkout-terms-note">상품별 유효기간과 이용 조건은 발급된 상품 안내 및 관계 법령이 우선합니다.</p>
        <button className="button primary" onClick={cancel} type="button">
          내용을 확인했습니다
        </button>
      </div>
    </dialog>
  )
}
const ProductPanel = ({ balance, client, product, status }: { readonly balance: Todo26LiveBalance; readonly client: Todo26LiveClient; readonly product: Todo26LiveProduct; readonly status: PhoneVerificationStatus }) => {
  const router = useRouter()
  const [acknowledged, setAcknowledged] = useState(false)
  const [pending, setPending] = useState(false)
  const [message, setMessage] = useState("")
  const [showCancellationTerms, setShowCancellationTerms] = useState(false)
  const affordable = liveProductAffordability(product, balance) === "AFFORDABLE"
  const eligible = affordable && status.checkoutEligibility === "ELIGIBLE" && status.maskedRecipient !== null
  const needsPhoneVerification = status.checkoutEligibility === "PHONE_VERIFICATION_REQUIRED" || status.checkoutEligibility === "PHONE_REVERIFICATION_REQUIRED" || status.maskedRecipient === null
  const unavailableReason = !affordable
    ? `보유 포인트가 ${number.format(Math.max(0, product.priceWon - balance.availableWon))}포인트 부족합니다.`
    : status.checkoutEligibility === "PHONE_REVERIFICATION_REQUIRED"
      ? "휴대전화 재확인이 필요합니다. 휴대전화 관리에서 다시 확인해 주세요."
      : status.checkoutEligibility === "PHONE_VERIFICATION_REQUIRED" || status.maskedRecipient === null
        ? "상품을 받으려면 휴대전화 인증이 필요합니다."
        : status.checkoutEligibility === "BLOCKED"
          ? "현재 상품 교환 기능이 운영 설정으로 일시 중지되어 있습니다. 포인트나 휴대전화 문제가 아니며, 서비스가 다시 열리면 교환할 수 있습니다."
          : "현재 이 상품을 교환할 수 없습니다. 잠시 후 다시 확인해 주세요."
  const prepare = async (): Promise<void> => {
    if (!eligible || pending) return
    if (!acknowledged) {
      setMessage("계속하려면 차감 포인트와 취소 조건 확인에 체크해 주세요.")
      return
    }
    setPending(true)
    setMessage("")
    try {
      router.push((await client.prepareRedemption(product.internalProductId)).nextRoute)
    } catch (error: unknown) {
      setMessage(errorCopy(error))
    } finally {
      setPending(false)
    }
  }
  return (
    <div className="product-checkout-layout product-checkout-redesign" data-todo26-product={product.internalProductId}>
      {<RedesignProductDetail availableWon={balance.availableWon} product={{ available: true, category: product.supplierCode, id: product.internalProductId, imageUrl: product.imageUrl, name: product.name, price: `${number.format(product.priceWon)}원`, priceWon: product.priceWon }} recipient={status.maskedRecipient} />}
      <section className="todo26-panel" data-todo26-checkout>
        {null}
        {!affordable ? (
          <div className="checkout-disabled-state">
            <output>{unavailableReason}</output>
            <button className="button primary" disabled type="button">
              포인트 부족
            </button>
          </div>
        ) : needsPhoneVerification ? (
          <div className="checkout-disabled-state">
            <output>{unavailableReason}</output>
            <Link className="button secondary" href={`/my/phone?next=${encodeURIComponent(`/products/${product.internalProductId}`)}`}>
              휴대전화 확인하기
            </Link>
          </div>
        ) : status.checkoutEligibility !== "ELIGIBLE" ? (
          <div className="checkout-disabled-state">
            <output>{unavailableReason}</output>
            <button className="button primary" disabled type="button">
              현재 교환 불가
            </button>
          </div>
        ) : (
          <div className="action-area">
            <div className="checkout-consent">
              <label className="check-row">
                <input checked={acknowledged} onChange={(event) => setAcknowledged(event.target.checked)} type="checkbox" />
                <span>차감 포인트와 취소·환불 조건을 확인했습니다.</span>
              </label>
              <button className="checkout-terms-trigger" onClick={() => setShowCancellationTerms(true)} type="button">
                취소·환불 조건 보기
              </button>
            </div>
            <button aria-busy={pending} aria-disabled={!acknowledged || pending} className="button primary" disabled={pending} onClick={() => void prepare()} type="button">
              {pending ? "교환 준비 중" : "교환하기"}
            </button>
            <CheckoutCancellationTermsModal onClose={() => setShowCancellationTerms(false)} open={showCancellationTerms} />
          </div>
        )}
        <ActionFeedbackSheet message={message} onClose={() => setMessage("")} title="교환 요청을 확인해 주세요" />
      </section>
    </div>
  )
}
const ConfirmPanel = ({ client, quote, status }: { readonly client: Todo26LiveClient; readonly quote: OwnedRedemptionQuoteResponse; readonly status: PhoneVerificationStatus }) => {
  const router = useRouter()
  const [acknowledged, setAcknowledged] = useState(false)
  const [pending, setPending] = useState(false)
  const [message, setMessage] = useState("")
  const [capExhausted, setCapExhausted] = useState(false)
  const [showCancellationTerms, setShowCancellationTerms] = useState(false)
  const expired = Date.parse(quote.expiresAt) <= Date.now()
  const eligible = !expired && !capExhausted && status.checkoutEligibility === "ELIGIBLE" && status.maskedRecipient !== null
  const confirm = async (): Promise<void> => {
    if (!eligible || pending) return
    if (!acknowledged) {
      setMessage("계속하려면 발급 및 취소 조건 확인에 체크해 주세요.")
      return
    }
    setPending(true)
    setMessage("")
    try {
      router.push((await client.confirmRedemption(quote.quoteId)).nextRoute)
    } catch (error: unknown) {
      if (error instanceof Todo26LiveHttpError && (error.code === "KT_PROVIDER_NOT_READY" || error.code === "PILOT_LIMIT_EXCEEDED" || error.code === "KT_ISSUANCE_DISABLED" || error.code === "T26_CHECKOUT_DISABLED")) setCapExhausted(true)
      setMessage(errorCopy(error))
    } finally {
      setPending(false)
    }
  }
  return (
    <section className="todo26-panel" data-todo26-confirm>
      <header className="todo26-panel-heading">
        <h2>{capExhausted ? "발급 한도 소진" : "최종 확인"}</h2>
      </header>
      <dl className="checkout-summary todo26-details">
        <div>
          <dt>차감 포인트</dt>
          <dd>
            <PointAmount amount={quote.amountWon} />
          </dd>
        </div>
        <div>
          <dt>받는 번호</dt>
          <dd>{status.maskedRecipient ?? "휴대전화 확인 필요"}</dd>
        </div>
        <div>
          <dt>견적 유효시간</dt>
          <dd>{new Intl.DateTimeFormat("ko-KR", { dateStyle: "medium", timeStyle: "short" }).format(new Date(quote.expiresAt))}</dd>
        </div>
      </dl>
      <div className="checkout-consent">
        <label className="check-row">
          <input checked={acknowledged} onChange={(event) => setAcknowledged(event.target.checked)} type="checkbox" />
          <span>발급 요청 후 취소·환불이 제한될 수 있음을 확인했습니다.</span>
        </label>
        <button className="checkout-terms-trigger" onClick={() => setShowCancellationTerms(true)} type="button">
          취소·환불 조건 보기
        </button>
      </div>
      <button aria-busy={pending} aria-disabled={!eligible || !acknowledged || pending} className="button primary" disabled={!eligible || pending} onClick={() => void confirm()} type="button">
        {pending ? "교환 확정 중" : expired ? "견적 만료" : capExhausted ? "한도 소진" : "교환 확정"}
      </button>
      <CheckoutCancellationTermsModal onClose={() => setShowCancellationTerms(false)} open={showCancellationTerms} />
      <ActionFeedbackSheet message={message} onClose={() => setMessage("")} title="교환 요청을 확인해 주세요" />
    </section>
  )
}
export const lifecyclePresentation = (
  lifecycle: OwnedRedemptionLifecycle,
): {
  readonly description: string
  readonly heading: string
  readonly tone: string
} => {
  switch (lifecycle) {
    case "PREPARING":
      return { description: "교환 요청을 안전하게 준비하고 있습니다. 같은 요청을 다시 보내지 마세요.", heading: "교환을 준비하고 있습니다", tone: "info" }
    case "ISSUING":
      return { description: "상품 발급 결과를 확인하고 있습니다. 완료될 때까지 같은 요청을 다시 보내지 마세요.", heading: "상품을 발급하고 있습니다", tone: "info" }
    case "SUCCESS":
      return { description: "교환이 완료되었습니다. 발급 결과는 내 상품권에서 확인할 수 있습니다.", heading: "교환이 완료되었습니다", tone: "success" }
    case "FAILURE":
      return { description: "교환을 완료하지 못했습니다. 사용 보류된 포인트의 복원 내역을 확인해 주세요.", heading: "교환을 완료하지 못했습니다", tone: "error" }
    case "NEEDS_REVIEW":
      return { description: "공급사 결과를 확인 중입니다. 안전을 위해 재발급이나 취소를 자동 실행하지 않습니다.", heading: "교환 결과를 확인하고 있습니다", tone: "warning" }
    case "CAP_EXHAUSTED":
      return { description: "현재 발급 한도가 소진되었습니다. 포인트는 사용되지 않습니다.", heading: "발급 한도가 소진되었습니다", tone: "warning" }
  }
}
const LifecyclePanel = ({ redemption }: { readonly redemption: OwnedRedemptionLifecycleResponse }) => {
  const presentation = lifecyclePresentation(redemption.lifecycle)
  return (
    <section aria-live="polite" className={`todo26-panel is-${presentation.tone}`} data-owned-lifecycle={redemption.lifecycle}>
      <p className="eyebrow">교환 상태</p>
      <h2>{presentation.heading}</h2>
      <p>{presentation.description}</p>
      <p>주문 확인 번호 ·••••-{redemption.orderId.slice(-4)}</p>
      <div className="destination-links">
        <Link className="button secondary" href="/orders">
          주문 내역
        </Link>
        {redemption.lifecycle === "SUCCESS" ? (
          <Link className="button primary" href="/vouchers">
            내 상품권
          </Link>
        ) : redemption.lifecycle === "NEEDS_REVIEW" ? (
          <Link className="button secondary" href="/support/inquiries">
            문의하기
          </Link>
        ) : null}
      </div>
    </section>
  )
}
export const Todo26LiveJourney = ({ pathname, route, search }: { readonly pathname: string; readonly route: UserRoute; readonly search: string }) => {
  const router = useRouter()
  const client = useMemo(() => createTodo26LiveClient(), [])
  const [reload, setReload] = useState(0)
  const [loaded, setLoaded] = useState<LoadState>({ kind: "loading" })
  useEffect(() => {
    void reload
    let active = true
    setLoaded({ kind: "loading" })
    void loadScreen(client, pathname, route, search)
      .then((screen) => {
        if (active) setLoaded({ kind: "loaded", screen })
      })
      .catch((error: unknown) => {
        if (!active) return
        reportOperationalFailure("LOAD", error)
        if (error instanceof Todo26LiveHttpError && (error.status === 401 || error.code === "T10_E_SESSION" || error.code === "T26_WEB_SESSION_REQUIRED")) {
          router.replace(`/login?returnTo=${encodeURIComponent(`${pathname}${search.length === 0 ? "" : `?${search}`}`)}`)
          return
        }
        setLoaded({ error, kind: "failed" })
      })
    return () => {
      active = false
    }
  }, [client, pathname, reload, route, router, search])
  useEffect(() => {
    if (loaded.kind !== "loaded" || loaded.screen.kind !== "lifecycle" || (loaded.screen.redemption.lifecycle !== "PREPARING" && loaded.screen.redemption.lifecycle !== "ISSUING")) return
    let active = true
    const timer = setTimeout(() => {
      void client
        .getRedemption(loaded.screen.kind === "lifecycle" ? loaded.screen.redemption.orderId : "")
        .then((redemption) => {
          if (active) setLoaded({ kind: "loaded", screen: { kind: "lifecycle", redemption } })
        })
        .catch((error: unknown) => {
          if (active) {
            reportOperationalFailure("POLL", error)
            setLoaded({ error: new Todo26LiveNetworkError(), kind: "failed" })
          }
        })
    }, 2500)
    return () => {
      active = false
      clearTimeout(timer)
    }
  }, [client, loaded])
  if (loaded.kind === "loading") return <JourneyLoading route={route} />
  return (
    <article className="journey-card todo26-journey" data-app-content-path={pathname} data-route-id={route.id} data-route-kind={route.kind} data-todo26-screen={loaded.kind === "loaded" ? loaded.screen.kind : loaded.kind}>
      {loaded.kind === "loaded" && loaded.screen.kind === "product" ? null : (
        <header className="journey-heading">
          <h1>{route.title}</h1>
          <p>{route.description}</p>
        </header>
      )}
      {loaded.kind === "failed" ? (
        <section className="todo26-panel is-error">
          <h2>상태를 확인하지 못했습니다</h2>
          <p>{errorCopy(loaded.error)}</p>
          <button className="button secondary" onClick={() => setReload((value) => value + 1)} type="button">
            다시 확인
          </button>
        </section>
      ) : loaded.screen.kind === "phone" ? (
        <PhonePanel client={client} initialStatus={loaded.screen.status} next={safeNext(new URLSearchParams(search).get("next"))} />
      ) : loaded.screen.kind === "product" ? (
        <ProductPanel balance={loaded.screen.balance} client={client} product={loaded.screen.product} status={loaded.screen.status} />
      ) : loaded.screen.kind === "confirm" ? (
        <ConfirmPanel client={client} quote={loaded.screen.quote} status={loaded.screen.status} />
      ) : (
        <LifecyclePanel redemption={loaded.screen.redemption} />
      )}
    </article>
  )
}
